import { FlexBox, Loader, LoaderType, Text } from '@fable/components'
import { css, useTheme } from '@fable/theme'
import { Book } from '@fable/types'
import { BooksCalloutPalette } from 'app/store/pages/bookstore/types'
import {
  CarouselControls,
  Carousel,
  useCarouselController,
} from 'components/carousel'
import { BookCarouselItem } from './BookCarouselItem'
import { TextHeaderSizing } from '@fable/components/bin/text/types'

export const BookCarousel = ({
  books,
  loading = false,
  title,
  subtitle,
  palette,
  bookLinkRel,
  titleSizing,
}: {
  title?: string
  subtitle?: string
  books: Book[]
  loading?: boolean
  palette?: BooksCalloutPalette
  bookLinkRel?: string
  titleSizing?: TextHeaderSizing
}) => {
  const { colors, breakpoints } = useTheme()
  const carouselController = useCarouselController()

  if (loading) {
    return (
      <div
        className={css`
          position: relative;
          height: 200px;
        `}
      >
        <Loader type={LoaderType.contained} />
      </div>
    )
  }

  if (!books?.length) return null

  return (
    <div
      className={css`
        position: relative;
        margin-top: 24px;
      `}
    >
      <FlexBox
        className={css`
          margin-bottom: 24px;
        `}
        justifyContent="space-between"
        alignItems="center"
      >
        <div>
          <Text
            tag="h2"
            type="header"
            sizing={titleSizing || { base: 'XS', mobileL: 'M' }}
            color={palette?.title || colors.uiGraphite}
          >
            {title}
          </Text>
          {subtitle && (
            <Text
              className={css`
                margin-top: 7px;
              `}
              type="body"
              sizing={{ base: 'S', mobileL: 'M' }}
              text={subtitle}
              color={palette?.subtitle || colors.uiGraphite}
            />
          )}
        </div>
        <CarouselControls
          controlProps={{ outline: true }}
          controller={carouselController}
        />
      </FlexBox>
      <Carousel
        controller={carouselController}
        options={{
          slides: { perView: 'auto', spacing: 23, origin: 0 },
          [`(min-width: ${breakpoints.mobileL}px)`]: {
            slides: { perView: 'auto', spacing: 30, origin: 'auto' },
          },
        }}
        childWidths={[
          { width: '157px', breakpoint: 'base' },
          { width: '162px', breakpoint: breakpoints.mobileL },
        ]}
        noOverflow
      >
        {books.map((book, i) => (
          <BookCarouselItem
            key={i}
            book={book}
            hidden={i < carouselController.currentSlide}
            rel={bookLinkRel}
          />
        ))}
      </Carousel>
    </div>
  )
}
